/* ============================================================
   Wyld Chics — dispatch nudge
   assets/wc-dispatch-ticket.js

   States, in plain language (Jonathan's rule, 2026-09-11):

     dispatch day before the morning run (default 11:00)
       "Priority morning dispatch"
       "Order within 2:14:05 for priority morning dispatch."

     dispatch day before the same-day cutoff (default 18:00)
       "Ships today"
       "Order within the next 4:12:30 for priority dispatch."

     final N minutes of that window (default 180)
       "Last chance today"  (kicker becomes a solid tag)
       "Only 41 minutes left to get it shipped today."

     after the cutoff, or a non-dispatch day, under 24h away
       "Ships tomorrow" / "Ships Monday"
       "Order in the next 14h 10m and it ships first thing tomorrow."

     more than 24h away (weekends)
       No countdown. A long timer is not urgency, it is noise.
       "Ships first thing Monday."

   COPY RULE: say when it SHIPS — that is yours to control.
   Never promise an arrival or delivery date; that belongs to
   the carrier and becomes a claim you can be held to.

   Time is read with Intl.DateTimeFormat.formatToParts, NOT by
   parsing toLocaleString output back into a Date, which is
   unreliable across browsers and returns Invalid Date in Safari.
   ============================================================ */
(function () {
  'use strict';

  var DAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var FULL = {
    sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday',
    thu: 'Thursday', fri: 'Friday', sat: 'Saturday'
  };
  var DAY_SECS = 86400;

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function parseClock(str, fh, fm) {
    var m = /^\s*(\d{1,2})\s*:\s*(\d{2})\s*$/.exec(str || '');
    if (m) {
      var h = parseInt(m[1], 10), mm = parseInt(m[2], 10);
      if (h >= 0 && h <= 23 && mm >= 0 && mm <= 59) return h * 3600 + mm * 60;
    }
    return fh * 3600 + fm * 60;
  }

  // Always shows seconds, so the number visibly moves. Over an hour:
  // "10:23:45". Under an hour it drops the hours field: "34:21".
  function human(secs) {
    var h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), sec = secs % 60;
    return (h ? h + ':' + pad(m) : m) + ':' + pad(sec);
  }

  function init(root) {
    if (root.dataset.wcBound) return;
    root.dataset.wcBound = '1';

    var cfg;
    try { cfg = JSON.parse(root.getAttribute('data-config') || '{}'); }
    catch (e) { cfg = {}; }

    var sameCut = parseClock(cfg.sameDayCutoff, 18, 0);
    var morning = parseClock(cfg.morningRun, 11, 0);
    // Saturday: Jonathan ships until 15:30 and (unless told otherwise) has no
    // separate morning run, so its "priority" target is the cutoff itself.
    var satCut = cfg.satCutoff ? parseClock(cfg.satCutoff, 15, 30) : sameCut;
    var satMorning = cfg.satMorning === true || cfg.satMorning === 'true';
    function cutoffFor(dow) { return dow === 6 ? satCut : sameCut; }
    function morningFor(dow) { return dow === 6 && !satMorning ? null : morning; }
    var farCountdown = cfg.farCountdown === true || cfg.farCountdown === 'true';
    var days = cfg.days === 'all' ? [0, 1, 2, 3, 4, 5, 6]
             : cfg.days === 'mon_sat' ? [1, 2, 3, 4, 5, 6]
             : [1, 2, 3, 4, 5];
    var finalSecs = Math.max(60, (parseInt(cfg.finalMinutes, 10) || 180) * 60);
    var barSecs = Math.max(finalSecs, (parseFloat(cfg.barHours) || 3) * 3600);

    var fmt;
    try {
      fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: cfg.timezone || 'America/Los_Angeles', hour12: false,
        weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      fmt.formatToParts(new Date());
    } catch (e) {
      root.style.display = 'none';  // unknown timezone: hide rather than show a wrong time
      return;
    }

    function nowParts() {
      var p = {}, list = fmt.formatToParts(new Date());
      for (var i = 0; i < list.length; i++) p[list[i].type] = list[i].value;
      var hr = parseInt(p.hour, 10) % 24;  // hour12:false can emit "24"
      return {
        dow: DAY.indexOf(String(p.weekday).toLowerCase()),
        secs: hr * 3600 + parseInt(p.minute, 10) * 60 + parseInt(p.second, 10)
      };
    }

    function state() {
      var p = nowParts();
      if (p.dow < 0) return null;
      var dispatchDay = days.indexOf(p.dow) !== -1;
      var todayMorning = morningFor(p.dow), todayCut = cutoffFor(p.dow);
      if (dispatchDay && todayMorning !== null && todayMorning < todayCut && p.secs < todayMorning) {
        return { mode: 'morning', day: 'today', remaining: todayMorning - p.secs };
      }
      if (dispatchDay && p.secs < todayCut) {
        // Saturday's window is "the weekend cutoff", not "ships today"
        return { mode: 'sameday', day: 'today', remaining: todayCut - p.secs, weekendCut: p.dow === 6 };
      }
      // Weekend wait: a non-dispatch day, or Saturday after its cutoff. No
      // timer and no "Monday": Jonathan does not want Saturday-night shoppers
      // reminded of the wait. Weekend orders are first out on the next run.
      if (!dispatchDay && (p.dow === 0 || p.dow === 6)) return { mode: 'weekend', day: '' };
      if (p.dow === 6 && dispatchDay) return { mode: 'weekend', day: '' };
      for (var add = 1; add <= 7; add++) {
        var d = (p.dow + add) % 7;
        if (days.indexOf(d) !== -1) {
          var target = morningFor(d) !== null ? morningFor(d) : cutoffFor(d);
          return {
            mode: 'priority',
            day: add === 1 ? 'tomorrow' : FULL[DAY[d]],
            remaining: add * DAY_SECS - p.secs + target,
            weekendCut: d === 6 && morningFor(6) === null   // Friday night counts to the weekend cutoff
          };
        }
      }
      return null;
    }

    var kicker = root.querySelector('[data-wc="kicker"]');
    var line = root.querySelector('[data-wc="line"]');
    var bar = root.querySelector('[data-wc="bar"]');
    var sr = root.querySelector('[data-wc="sr"]');

    function fill(tpl, vars) {
      return String(tpl || '').replace(/\{(time|day|minutes)\}/g, function (_, k) {
        return vars[k] != null ? vars[k] : '';
      });
    }

    // Build the sentence as text nodes with the countdown in its own <b>,
    // so merchant copy is never injected as HTML.
    // Only touch the DOM when the words change: with no countdown (weekends,
    // far-off runs) the sentence was rewritten every second for nothing
    // (Jonathan, 2026-09-19).
    var lastLine = null, lastKicker = null, lastSr = null;
    function paintLine(tpl, vars) {
      if (!line) return;
      var key = fill(tpl, vars);
      if (key === lastLine) return;
      lastLine = key;
      while (line.firstChild) line.removeChild(line.firstChild);
      var parts = String(tpl || '').split('{time}');
      for (var i = 0; i < parts.length; i++) {
        line.appendChild(document.createTextNode(fill(parts[i], vars)));
        if (i < parts.length - 1) {
          var b = document.createElement('b');
          b.className = 'wc-dispatch__time';
          b.textContent = vars.time;
          line.appendChild(b);
        }
      }
    }

    function render() {
      var s = state();
      if (!s) { root.style.display = 'none'; return; }

      var t = s.remaining || 0;
      var far = s.mode === 'weekend' || (t > DAY_SECS && !farCountdown);   // no countdown
      var isFinal = !far && t <= finalSecs;
      var vars = { time: human(t), day: s.day, minutes: Math.max(1, Math.round(t / 60)) };

      var k, msg;
      if (s.mode === 'weekend') {
        k = cfg.kickerWeekend;
        msg = cfg.msgWeekend;
      } else if (s.weekendCut) {
        k = cfg.kickerWeekendCutoff;
        msg = isFinal ? cfg.msgWeekendCutoffFinal : cfg.msgWeekendCutoff;
      } else if (s.mode === 'morning') {
        k = cfg.kickerMorning;
        msg = isFinal ? cfg.msgMorningFinal : cfg.msgMorning;
      } else if (s.mode === 'sameday') {
        k = isFinal ? cfg.kickerFinal : cfg.kickerSameDay;
        msg = isFinal ? cfg.msgSameDayFinal : cfg.msgSameDay;
      } else {
        k = cfg.kickerPriority;
        msg = far ? cfg.msgFar : (isFinal ? cfg.msgPriorityFinal : cfg.msgPriority);
      }

      var kText = fill(k, vars);
      if (kicker && kText !== lastKicker) { kicker.textContent = kText; lastKicker = kText; }
      paintLine(msg, vars);
      // One stable sentence for assistive tech instead of a per-second barrage.
      var srText = fill(msg, vars);
      if (sr && srText !== lastSr) { sr.textContent = srText; lastSr = srText; }

      root.classList.toggle('is-final', isFinal);

      if (bar) {
        var pct = far ? 0 : Math.min(100, Math.max(0, (1 - t / barSecs) * 100));
        bar.style.width = pct.toFixed(1) + '%';
      }

      root.setAttribute('data-ready', '');
    }

    render();
    var timer = setInterval(render, 1000);

    // The theme editor re-renders sections; don't leave orphan intervals running.
    document.addEventListener('shopify:section:unload', function (evt) {
      if (evt.target && evt.target.contains(root)) clearInterval(timer);
    });
  }

  function boot() {
    var nodes = document.querySelectorAll('.wc-dispatch[data-config]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
  // The cart drawer copy lives inside [data-cart-footer], which Alpine
  // morphs on every cart change; a replaced node needs binding again.
  ['shapes:cart:afteradditem', 'shapes:cart:cartqtychange', 'shapes:cart:update'].forEach(function (name) {
    window.addEventListener(name, function () { setTimeout(boot, 80); });
  });
  document.addEventListener('theme:cart-drawer:opening', boot);
  document.addEventListener('theme:cart-drawer:open', boot);
  // The drawer is teleported into #right-drawer-slot when Alpine gets to it,
  // and the footer is morphed on every change. Watch for unbound copies.
  if (window.MutationObserver) {
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        if (document.querySelector('.wc-dispatch[data-config]:not([data-wc-bound])')) boot();
      }, 80);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
